import type { UserAlertsDto } from "@intelligems/ig-types/dtos/users";
import type { PatchUserRequestBodyType } from "@intelligems/ig-types/functions/users/patch-user";
import type {
  LoginResponseBodyType,
  LoginResponseBodyUserType,
} from "@intelligems/ig-types/functions/user-auths/login";
import type { GetOrganizationResponseBodyType } from "@intelligems/ig-types/functions/organizations/get-organization";
import { api } from "@src/api/api";
import { organizationsKeys, userKeys } from "@src/api/queryKeys";
import { queryClient } from "@src/queryClient";

/**
 * Changes some of the logged-in user's alerts. The cached user changes at
 * once, so whatever reads an alert updates without waiting for the server.
 *
 * The user PATCH replaces `userAlerts` whole, so this sends the cached alerts
 * with the changes applied.
 */
export const setUserAlerts = (changes: Partial<UserAlertsDto>) => {
  const login = queryClient.getQueryData<LoginResponseBodyType>(userKeys.all);
  const organization =
    queryClient.getQueryData<GetOrganizationResponseBodyType>(
      organizationsKeys.one
    );
  if (login?.type !== "login" || !organization) {
    return;
  }
  const { user } = login;
  const userAlerts = { ...user.userAlerts, ...changes };
  const changed: LoginResponseBodyUserType = {
    ...login,
    user: { ...user, userAlerts },
  };
  queryClient.setQueryData<LoginResponseBodyType>(userKeys.all, changed);
  api
    .patch<unknown, PatchUserRequestBodyType>(`/v2/users/${user.id}`, {
      user: { id: user.id, organizationId: organization.org.id, userAlerts },
    })
    .catch(
      () => void queryClient.invalidateQueries({ queryKey: userKeys.all })
    );
};

/** Whether the logged-in user has yet to dismiss this one-time notice. */
export const isUserAlertPending = (key: keyof UserAlertsDto): boolean => {
  const login = queryClient.getQueryData<LoginResponseBodyType>(userKeys.all);
  return login?.type === "login" && login.user.userAlerts?.[key] !== false;
};
